import { useCallback, useEffect, useRef, useState } from 'react'
import { Send, X } from 'lucide-react'

/*
 * Native WhatsApp click-to-chat widget, scoped to whichever page mounts it.
 * Ported from the sister product (pressone-esim): same BSP phone-number API,
 * but the UI is built from this repo's design tokens. The wa.me link is a
 * real anchor whose href is ready before the click — embeds that call
 * window.open after an await get rejected by popup blockers.
 *
 * The BSP endpoint only reveals the number when the widget key has an active
 * WhatsApp number provisioned in the BSP portal; until then the panel shows
 * an "unavailable" notice instead of a dead send button.
 *
 * The fetch goes browser → bsp.ent.pressone.co directly, so the CSP in
 * main.py lists that origin under connect-src.
 */

const WIDGET_KEY = 'ww_live_1qmd8hj3p5ov5nnn9gxdv7jfgs4f'
const PHONE_ENDPOINT = `https://bsp.ent.pressone.co/api/v1/widget/phone-number/${WIDGET_KEY}`

const BUSINESS_NAME = 'PressOne Support'
const TAGLINE = 'Typically replies in a few minutes'
const GREETING = 'Hi 👋 How can we help you today?'

type PhoneState =
  | { status: 'idle' | 'loading' | 'unavailable' }
  | { status: 'ready'; phone: string }

function waHref(phone: string, message: string): string {
  const text = message.trim()
  return `https://wa.me/${phone}${text ? `?text=${encodeURIComponent(text)}` : ''}`
}

/* Official WhatsApp glyph — lucide dropped brand icons, so it's inlined. */
function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.241-.579-.486-.5-.668-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.064 2.875 1.213 3.074.149.198 2.095 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  )
}

export function WhatsAppWidget() {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [phone, setPhone] = useState<PhoneState>({ status: 'idle' })
  const inputRef = useRef<HTMLInputElement>(null)

  const fetchPhone = useCallback(async () => {
    setPhone({ status: 'loading' })
    try {
      const res = await fetch(PHONE_ENDPOINT)
      if (!res.ok) throw new Error(`widget phone lookup failed: ${res.status}`)
      const data = (await res.json()) as { data?: { phone_number?: string } }
      const digits = data?.data?.phone_number?.replace(/\D/g, '')
      if (!digits) throw new Error('widget phone lookup returned no number')
      setPhone({ status: 'ready', phone: digits })
    } catch {
      setPhone({ status: 'unavailable' })
    }
  }, [])

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev
      if (next && phone.status === 'idle') void fetchPhone()
      return next
    })
  }

  useEffect(() => {
    if (!open) return
    inputRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const ready = phone.status === 'ready'

  // The send control is an anchor so the navigation happens inside the
  // user's click gesture; Enter in the input reuses the same cached number.
  const openChat = () => {
    if (!ready) return
    window.open(waHref(phone.phone, message), '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-label={`Chat with ${BUSINESS_NAME} on WhatsApp`}
          className="fixed bottom-24 right-4 z-50 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-3 sm:right-6"
        >
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-green-soft text-brand-green">
              <WhatsAppGlyph className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-text-strong">{BUSINESS_NAME}</p>
              <p className="truncate text-xs text-muted">{TAGLINE}</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-text-strong"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-2 bg-bg/60 px-4 py-5">
            <div className="max-w-[85%] rounded-2xl rounded-tl-md border border-border bg-surface px-3.5 py-2.5 text-sm text-text shadow-1">
              {GREETING}
            </div>
            {phone.status === 'unavailable' && (
              <div className="mt-1 max-w-[85%] rounded-2xl rounded-tl-md border border-brand-gold/30 bg-warning-soft px-3.5 py-2.5 text-xs text-text">
                Live chat is unavailable right now.{' '}
                <button type="button" onClick={() => void fetchPhone()} className="font-medium underline underline-offset-2">
                  Try again
                </button>{' '}
                or email{' '}
                <a href="mailto:support@pressone.africa" className="font-medium underline underline-offset-2">
                  support@pressone.africa
                </a>
                .
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="flex items-center gap-2 border-t border-border bg-surface px-3 py-3">
            <input
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') openChat()
              }}
              placeholder="Type a message…"
              className="h-10 flex-1 rounded-lg border border-border bg-bg px-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30"
            />
            <a
              href={ready ? waHref(phone.phone, message) : undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Continue in WhatsApp"
              aria-disabled={!ready}
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#25D366] text-white transition-[background-color,transform] hover:bg-[#1faf53] active:scale-[0.96] ${
                ready ? '' : 'pointer-events-none opacity-50'
              }`}
            >
              {phone.status === 'loading' ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </a>
          </div>

          <p className="border-t border-border bg-surface px-4 py-2 text-center text-[11px] text-muted">
            Opens in WhatsApp — you chat from your own account.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-label={open ? 'Close WhatsApp chat' : 'Chat with us on WhatsApp'}
        aria-expanded={open}
        className="fixed bottom-5 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_1px_0_rgba(255,255,255,0.25)_inset,0_12px_28px_-10px_rgba(27,222,108,0.55)] transition-[background-color,transform] hover:bg-[#1faf53] active:scale-95 sm:right-6"
      >
        {open ? <X className="h-6 w-6" /> : <WhatsAppGlyph className="h-7 w-7" />}
      </button>
    </>
  )
}
